import { createSelector, createSlice } from '@reduxjs/toolkit';
import { logger } from '@core/utils';

const initialState = {
  user: null,
  csrfToken: null,
  isAuthenticated: false,
  sessionChecked: false,
};

/**
 * Auth slice for managing authentication state
 */
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    initializeAuth: (state) => {
      // Remove credentials persisted by versions predating HttpOnly cookies.
      try {
        localStorage.removeItem('token');
        localStorage.removeItem('userData');
      } catch (error) {
        logger.error('Error clearing legacy auth storage:', error);
      }
      state.sessionChecked = false;
    },
    setCredentials: (state, action) => {
      const { user, csrfToken } = action.payload;
      state.user = user;
      state.csrfToken = csrfToken;
      state.isAuthenticated = true;
      state.sessionChecked = true;
    },
    restoreSession: (state, action) => {
      state.user = action.payload.user;
      state.csrfToken = action.payload.csrfToken;
      state.isAuthenticated = true;
      state.sessionChecked = true;
    },
    // The signed-in user's own profile changed (e.g. their language).
    updateCurrentUser: (state, action) => {
      state.user = action.payload;
    },
    markSessionAnonymous: (state) => {
      state.user = null;
      state.csrfToken = null;
      state.isAuthenticated = false;
      state.sessionChecked = true;
    },
    logout: (state) => {
      state.user = null;
      state.csrfToken = null;
      state.isAuthenticated = false;
      state.sessionChecked = true;
    },
  },
});

export const { initializeAuth, setCredentials, restoreSession, updateCurrentUser, markSessionAnonymous, logout } = authSlice.actions;

export default authSlice.reducer;

// Selectors
export const selectCurrentUser = (state) => state.auth.user;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectSessionChecked = (state) => state.auth.sessionChecked;
export const selectCsrfToken = (state) => state.auth.csrfToken;
export const selectIsStaff = (state) => Boolean(state.auth.user?.is_staff);
export const selectIsSuperuser = (state) => Boolean(state.auth.user?.is_superuser);
export const selectIsImpersonating = (state) => Boolean(state.auth.user?.is_impersonating);

// Strict lookup: a missing or unknown active_organization yields null so no
// role is silently granted or hidden by falling back to another membership.
export const selectActiveOrganization = createSelector(
  [selectCurrentUser],
  (user) => user?.organizations?.find((org) => org.uuid === user.active_organization) ?? null,
);
export const selectActiveRole = createSelector(
  [selectActiveOrganization],
  (organization) => organization?.role ?? null,
);
export const selectIsGuest = createSelector(
  [selectActiveRole],
  (role) => role === 'guest',
);
export const selectCanManageOrganization = createSelector(
  [selectActiveRole],
  (role) => role === 'owner' || role === 'admin',
);
